import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiSend } from "../lib/api.js";
import type { MemberDto, MemberRole } from "./types.js";

/**
 * Sprint 24 Wave C — PR-C2: admin-console mutation that updates a
 * member's role.
 *
 * Wraps the existing Sprint 7 endpoint
 * `PATCH /v1/admin/members/:id/role`. Permission is enforced
 * server-side by `MembersService.setRole` (`tenant.members.write`).
 * The UI hides the affordance behind `useAccess().can("tenant.members.write")`
 * to save a round-trip; the API check remains the source of truth.
 *
 * On success the members query is invalidated so the role chip and
 * the per-role aggregate refresh.
 *
 * Safety: never logs or surfaces the raw API error body. The caller
 * renders a localized error message (`admin.members.error.role_change`)
 * and the actual error stays in the React Query cache for debugging.
 */

interface SetMemberRoleVars {
  memberId: string;
  role: MemberRole;
}

export function useSetMemberRole() {
  const qc = useQueryClient();
  return useMutation<{ member: MemberDto }, Error, SetMemberRoleVars>({
    mutationFn: ({ memberId, role }) =>
      apiSend<{ member: MemberDto }>("PATCH", `/v1/admin/members/${memberId}/role`, {
        role,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "members"] });
    },
  });
}
